import { Shimmer } from "@/components/ai-elements/shimmer";
import { ChatElapsedTime } from "@/components/chat/chat-dialog-primitives";
import {
  RollingSegments,
  StepRow,
} from "@/components/chat/chat-thinking-step";
import { useKeptDomains } from "@/hooks/useKeptDomains";
import {
  COLLAPSE_ANIMATION_CLASS,
  countSegments,
  shownSeconds,
  type CounterSegment,
  type RowTime,
} from "@/lib/chat-thinking-format";
import { ResearchDomainsRow } from "@/components/chat/research-domains-row";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useTranslation } from "@/hooks/useTranslation";
import {
  CLARIFICATION_ANSWERED_PART_TYPE,
  isStreamedPart,
  liveResearchAction,
  SEARCH_TOOL_TYPES,
  type ResearchPart,
} from "@/lib/chat-render-items";
import {
  buildThinkingTrace,
  computeTraceDurations,
  SEARCH_STEP_KINDS,
  sumCounts,
  withTimelineSteps,
  type ThinkingStep,
  type ThinkingTraceOptions,
} from "@/lib/chat-thinking-trace";
import { cn } from "@/lib/utils";
import { formatDuration } from "@/utils/time";
import type { ChatTraceTimelineData } from "@shaarp/common";
import type { ToolUIPart } from "ai";
import {
  BrainIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  GlobeIcon,
  XCircleIcon,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useRef, useState } from "react";

/** How close to the bottom still counts as following the newest step. */
const PINNED_THRESHOLD_PX = 8;

type CardState = "working" | "done" | "failed";

/**
 * The timeline stays on the newest step. It follows the size of its rows
 * rather than the moment one is added — a row opens its height after it
 * mounts, and scrolling at mount stopped short of it — and lets go while the
 * reader has scrolled up to reread a step.
 */
function useTimelineFollow(isOpen: boolean) {
  const timelineRef = useRef<HTMLDivElement>(null);
  const isPinnedRef = useRef(true);

  useEffect(() => {
    const timeline = timelineRef.current;
    const list = timeline?.firstElementChild;
    if (!timeline || !list || typeof ResizeObserver === "undefined") return;
    const follow = (): void => {
      if (isPinnedRef.current) timeline.scrollTop = timeline.scrollHeight;
    };
    follow();
    const observer = new ResizeObserver(follow);
    observer.observe(list);
    return () => observer.disconnect();
  }, [isOpen]);

  const onScroll = (): void => {
    const timeline = timelineRef.current;
    if (!timeline) return;
    isPinnedRef.current =
      timeline.scrollHeight - timeline.scrollTop - timeline.clientHeight <
      PINNED_THRESHOLD_PX;
  };

  return { timelineRef, onScroll };
}

/**
 * How far this browser's clock is ahead of the server's, read each time the
 * timeline gains an event: a running row counts on from the server's start of
 * it, shown on this clock. Null until the timeline has an event.
 */
function useServerClockOffset(timeline: ChatTraceTimelineData | null): number | null {
  const [offset, setOffset] = useState<number | null>(null);
  const events = timeline?.events ?? [];
  const lastAt = events.length > 0 ? events[events.length - 1].at : null;

  useEffect(() => {
    if (lastAt !== null) setOffset(Date.now() - lastAt);
  }, [lastAt]);

  return offset;
}

/** One turn the card shows, and the rows it fills. */
type TimedTurn = {
  readonly timeline: ChatTraceTimelineData | null;
  readonly fromIndex: number;
  readonly toIndex: number;
};

/**
 * The time each row shows — what it ran, from its turn's stored timeline, and
 * since when a row at work runs on this browser's clock — and the wait the
 * header shows, the time calls shared counted once. Before the last turn's
 * first event — the pending answer — its first step runs from the turn's
 * start.
 *
 * Each turn is timed from its own timeline, so the time the reader took to
 * answer a question between two turns is counted nowhere. A turn that is over
 * — carried on by the next one, or no longer streamed — stopped at its last
 * event.
 */
function useRowTimes({
  steps,
  turns,
  isLive,
  turnStartedAtMs,
  activeStep,
}: {
  steps: readonly ThinkingStep[];
  turns: readonly TimedTurn[];
  isLive: boolean;
  turnStartedAtMs: number | undefined;
  activeStep: ThinkingStep | null;
}): { rows: Readonly<Record<string, RowTime>>; total: RowTime } {
  const lastTurn = turns[turns.length - 1];
  const offset = useServerClockOffset(lastTurn.timeline);
  const rows: Record<string, RowTime> = {};
  let totalFinishedMs = 0;
  let totalRunningSince: number | undefined;
  for (const step of steps) rows[step.id] = { finishedMs: 0 };
  for (const turn of turns) {
    const { timeline } = turn;
    if (!timeline) continue;
    const isShownLive = turn === lastTurn && isLive;
    const durations = computeTraceDurations(steps.slice(0, turn.toIndex), timeline, {
      fromIndex: turn.fromIndex,
      isOver: !isShownLive,
    });
    for (const [id, ms] of Object.entries(durations.finishedMs)) {
      rows[id] = { finishedMs: ms };
    }
    totalFinishedMs += durations.total.finishedMs;
    if (!isShownLive || offset === null) continue;
    for (const running of durations.running) {
      rows[running.id] = { ...rows[running.id], runningSince: running.since + offset };
    }
    if (durations.total.runningSince !== null) {
      totalRunningSince = durations.total.runningSince + offset;
    }
  }
  if (!lastTurn.timeline && isLive && activeStep && turnStartedAtMs !== undefined) {
    rows[activeStep.id] = { ...rows[activeStep.id], runningSince: turnStartedAtMs };
    totalRunningSince = turnStartedAtMs;
  }
  return { rows, total: { finishedMs: totalFinishedMs, runningSince: totalRunningSince } };
}

/**
 * The card's state at a glance: its icon breathes while the work runs, and
 * turns into a check — or a cross — when it ends.
 */
function HeaderStatusIcon({ icon: Icon, state }: { icon: typeof GlobeIcon; state: CardState }) {
  const shouldReduceMotion = useReducedMotion();
  const swap = shouldReduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, scale: 0.6, filter: "blur(4px)" },
        animate: { opacity: 1, scale: 1, filter: "blur(0px)" },
        exit: { opacity: 0, scale: 0.6, filter: "blur(4px)" },
      };
  return (
    <span className="relative grid size-4 shrink-0 place-items-center" aria-hidden>
      <AnimatePresence initial={false} mode="popLayout">
        <motion.span
          key={state}
          className="grid place-items-center"
          {...swap}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          {state === "working" ? (
            <Icon className="size-4 text-majorelle animate-pulse motion-reduce:animate-none" />
          ) : state === "failed" ? (
            <XCircleIcon className="size-4 text-red-600" />
          ) : (
            <CheckCircleIcon className="size-4 text-green-600" />
          )}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

/**
 * The card's one line: what it is at, how long it has worked, and — folded —
 * what it counted. The total is the wait itself: calls that ran side by side
 * count once, so it can be less than the rows added up, and it never goes
 * back.
 */
function TraceHeader({
  state,
  icon,
  isOpen,
  title,
  activeLabel,
  finishedMs,
  runningSince,
  summary,
}: {
  state: CardState;
  icon: typeof GlobeIcon;
  isOpen: boolean;
  title: string;
  activeLabel: string | null;
  finishedMs: number;
  runningSince: number | undefined;
  summary: readonly CounterSegment[];
}) {
  const seconds = shownSeconds(finishedMs);
  return (
    <CollapsibleTrigger className="flex w-full items-center gap-2.5 p-3 text-left text-sm text-muted-foreground transition-colors hover:text-foreground">
      <HeaderStatusIcon icon={icon} state={state} />
      <span className="flex min-w-0 items-baseline gap-2">
        {state === "working" ? (
          <Shimmer className="shrink-0 font-medium">{title}</Shimmer>
        ) : (
          <span className="min-w-0 truncate font-medium text-foreground">{title}</span>
        )}
        {/* A ticking clock in a button's name is read out every second. */}
        <span className="shrink-0 text-xs tabular-nums" aria-hidden>
          {runningSince !== undefined ? (
            <ChatElapsedTime startedAtMs={runningSince - finishedMs} />
          ) : (
            seconds > 0 && formatDuration(seconds)
          )}
        </span>
        {/* The step at work, while its row is out of sight: open, the row
            says it, and the header would only repeat it. */}
        {!isOpen && activeLabel && (
          <span className="min-w-0 truncate text-xs">{activeLabel}</span>
        )}
      </span>
      {/* Folded, the climbing counters are the proof the work goes on. */}
      {!isOpen && summary.length > 0 && (
        <span className="min-w-0 truncate text-xs tabular-nums">
          <RollingSegments segments={summary} />
        </span>
      )}
      <ChevronDownIcon
        className={cn("ml-auto size-4 shrink-0 transition-transform", isOpen && "rotate-180")}
      />
    </CollapsibleTrigger>
  );
}

/**
 * The work behind an answer, as one card from the moment the question is sent:
 * the steps the turn went through, in the order they happened, each named in
 * the reader's language and timed from the turn's stored timeline.
 *
 * Open while the reader waits, folded to one line once the answer is under
 * way — writing the answer is not a step. What the model wrote on its way
 * (reasoning, queries, a tool's criteria) is never on screen by default: it
 * is often in English and reads as noise, or worse, to someone who did not
 * pick that language, so each row keeps it behind its own detail.
 *
 * A turn that worked, then asked a question, is carried on by the next
 * answer: the card shows the two turns' work as one timeline, the answers
 * taken in between them, and follows the live state of the second.
 */
export function ChatThinkingTrace({
  messageId,
  parts,
  timeline = null,
  isLive,
  isTrailing,
  hasAnswerText,
  answersClarification,
  isAnswerUnderway = hasAnswerText,
  turnStartedAtMs,
  headerLabelOverride,
  continuation,
}: ThinkingTraceOptions & {
  messageId: string;
  parts: readonly ResearchPart[];
  /** The turn's stored timeline: the rows are timed from it. */
  timeline?: ChatTraceTimelineData | null;
  /**
   * The answer written after the card is plainly the answer, not a line that
   * may still announce the next call: the card folds then. Defaults to
   * `hasAnswerText`.
   */
  isAnswerUnderway?: boolean;
  /** Start of the turn, for the pending answer's clock; absent on a reload. */
  turnStartedAtMs?: number;
  /** Names the wait in place of the step — a compaction is not a step. */
  headerLabelOverride?: string;
  /**
   * The work of the answer that carried this turn on after its question —
   * none yet while that answer is awaited. The live flags are that answer's.
   */
  continuation?: {
    parts: readonly ResearchPart[];
    timeline: ChatTraceTimelineData | null;
  };
}) {
  const { t } = useTranslation();
  const ownParts = withTimelineSteps(parts, timeline);
  const trace = buildThinkingTrace(
    continuation
      ? [
          ...ownParts,
          { part: { type: CLARIFICATION_ANSWERED_PART_TYPE }, index: -1 },
          ...withTimelineSteps(continuation.parts, continuation.timeline),
        ]
      : ownParts,
    { isLive, isTrailing, hasAnswerText, answersClarification }
  );
  const { activeStep, steps } = trace;
  // The last one: the turn may itself open on answers to an earlier question.
  const answeredIndex = steps.map((step) => step.kind).lastIndexOf("clarification");
  const turns: TimedTurn[] = continuation
    ? [
        { timeline, fromIndex: 0, toIndex: answeredIndex },
        {
          timeline: continuation.timeline,
          fromIndex: answeredIndex + 1,
          toIndex: steps.length,
        },
      ]
    : [{ timeline, fromIndex: 0, toIndex: steps.length }];
  const { rows: times, total } = useRowTimes({
    steps,
    turns,
    isLive,
    turnStartedAtMs,
    activeStep,
  });

  // Still the wait the reader watches — open, and named as work in progress —
  // until the answer written after it is plainly under way, even while a line
  // announcing the next call pauses every row's clock.
  const isAnswerShowing = isAnswerUnderway && !isTrailing;
  const isWorking = isLive && !isAnswerShowing;
  // The reader's own click wins over the automatic fold, both ways.
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const isOpen = userOpen ?? isWorking;
  const { timelineRef, onScroll } = useTimelineFollow(isOpen);

  const toolParts = [...parts, ...(continuation?.parts ?? [])]
    .map(({ part }) => part)
    .filter(isStreamedPart) as ToolUIPart[];
  const searchParts = toolParts.filter((part) => SEARCH_TOOL_TYPES.has(part.type));
  const isInterrupted = steps.some((step) => step.status === "interrupted");
  // Only a turn that stopped gets the cross. A failed call is read by the
  // model, which carries on — it says so on its own row — and a red header
  // over an answer that came through read as the answer having failed.
  const state: CardState = isWorking || activeStep
    ? "working"
    : isInterrupted
      ? "failed"
      : "done";

  // The page a running read is on says more than "Reading pages". A search's
  // query is the model's wording, so it never names the step.
  const liveAction = trace.isRunning ? liveResearchAction(searchParts) : null;
  const readingLabel =
    liveAction?.kind === "visit"
      ? t("chat.message.research.liveVisit", { host: liveAction.host })
      : liveAction?.kind === "read"
        ? t("chat.message.research.liveRead", { count: liveAction.count })
        : undefined;

  const activeLabel = activeStep ? t(`chat.thinkingTrace.steps.${activeStep.kind}`) : null;
  const searchSteps = steps.filter((step) => SEARCH_STEP_KINDS.has(step.kind));
  const toolNames = [
    ...new Set(
      steps
        .filter(
          (step) =>
            !SEARCH_STEP_KINDS.has(step.kind) &&
            step.kind !== "reflection" &&
            step.kind !== "clarification"
        )
        .map((step) => t(`chat.thinkingTrace.tools.${step.kind}`))
    ),
  ];
  const summary: CounterSegment[] = [
    ...countSegments(sumCounts(searchSteps), t, trace.isRunning),
    ...toolNames.map((name) => ({ key: `tool-${name}`, text: name })),
  ];
  const domains = useKeptDomains(searchSteps.flatMap((step) => step.domains));
  const title =
    state === "working"
      ? (headerLabelOverride ?? t("chat.thinkingTrace.running"))
      : isInterrupted
        ? t("chat.message.research.interrupted")
        : t("chat.thinkingTrace.done");

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setUserOpen}
      className="w-full overflow-hidden rounded-xl border bg-background not-prose"
    >
      <TraceHeader
        state={state}
        icon={searchSteps.length > 0 ? GlobeIcon : BrainIcon}
        isOpen={isOpen}
        title={title}
        activeLabel={headerLabelOverride ? null : activeLabel}
        finishedMs={total.finishedMs}
        runningSince={total.runningSince}
        summary={summary}
      />
      {/* Announced once per step change, not on every shimmer frame. */}
      <span className="sr-only" aria-live="polite">
        {activeLabel ? t("chat.thinkingTrace.liveRegion", { step: activeLabel }) : ""}
      </span>
      {/* Outside the panel: folding the card must not cost the reader the one
          moving proof that the search is still finding things. */}
      {!isOpen && trace.isRunning && domains.length > 0 && <ResearchDomainsRow domains={domains} />}
      <CollapsibleContent className={COLLAPSE_ANIMATION_CLASS}>
        {/* Bounded the same way while the work runs and once it is over: a
            long turn writes a dozen rows, and a card that grew by their whole
            height made the page jump when it folded. A short list never
            reaches the bound, and shows no scrollbar. */}
        <div
          ref={timelineRef}
          onScroll={onScroll}
          tabIndex={0}
          role="region"
          aria-label={t("chat.thinkingTrace.timeline")}
          className="max-h-56 overflow-y-auto overscroll-y-contain focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ol className="px-3 pb-1">
            <AnimatePresence initial={false}>
              {steps.map((step, stepIndex) => (
                <StepRow
                  key={step.id}
                  step={step}
                  time={times[step.id]}
                  messageId={messageId}
                  isLive={isLive}
                  isLast={stepIndex === steps.length - 1}
                  isTraceRunning={trace.isRunning}
                  liveLabel={
                    step.kind === "readPages" && step.status === "running"
                      ? readingLabel
                      : undefined
                  }
                />
              ))}
            </AnimatePresence>
          </ol>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
